// The link wizards' endpoints — both directions, shared by contracts and incoming-shipments.
import { baseApi } from '@/lib/api/baseApi';
import {
  TAG_TYPES,
  linkedShipmentsTag,
  linkableShipmentsTag,
  linkedContractsTag,
  linkableContractsTag,
  linkedShipmentsForDeclarationTag,
  linkableShipmentsForDeclarationTag,
} from '@/lib/api/tags';
import { processServerError } from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import {
  processLinkableShipments,
  processLinkableContracts,
  processShipmentDiscrepancy,
  buildShipmentSearchInput,
  buildContractSearchInput,
  processLinkableOrders,
  buildOrderSearchInput,
  buildLinkableOrderOrderBy,
  processLinkableShipmentsForOrder,
  buildLinkableShipmentSearchInput,
  buildLinkableShipmentOrderBy,
} from '@/lib/mappers/links';
import { buildShipmentFiltersInput, buildContractFiltersInput } from '@/lib/mappers/filters';

export const LINKABLE_SHIPMENTS_FOR_CONTRACT_QUERY = `
  query LinkableShipmentsForContract($contractId: ID!, $limit: Int, $page: Int, $search: ShipmentSearch, $filters: ShipmentFilters, $orderBy: [ShipmentOrderBy!]) {
    linkableShipmentsForContract(contractId: $contractId, limit: $limit, page: $page, search: $search, filters: $filters, orderBy: $orderBy) {
      nodes {
        group
        linkedContractIds
        sustainabilityMismatches { attribute message }
        assignedQuantityWorking
        assignedQuantityDeclaration
        assignedQuantityTracking
        shipment {
          id
          shipmentId
          workingUnit
          declarationUnit
          trackingUnit
          totalContractsLinked
          shipmentQuantityWorking
          shipmentQuantityDeclaration
          shipmentQuantityTracking
          unassignedContractQuantityWorking
          unassignedContractQuantityDeclaration
          unassignedContractQuantityTracking
          ghgEmissions
          ghgEmissionSavings
          rawMaterialType
          countryOfOrigin
          feedstockCategory
          preferredAllocation
          dateOfDispatch
          discrepancyCount
        }
      }
      pageInfo { total page limit hasNextPage hasPreviousPage isEmpty }
    }
  }
`;

// Link orders ▸ Step 1 + Fulfil ▸ Select order — the server derives the match from the shipment, so only its id travels.
export const LINKABLE_ORDERS_FOR_SHIPMENT_QUERY = `
  query LinkableOrdersForOutgoingShipment($shipmentId: ID!, $limit: Int, $page: Int, $filters: PendingOrderFilters, $search: PendingOrderSearch, $orderBy: [PendingOrderOrderBy!]) {
    linkableOrdersForOutgoingShipment(shipmentId: $shipmentId, limit: $limit, page: $page, filters: $filters, search: $search, orderBy: $orderBy) {
      nodes {
        assignedQuantityWorking
        assignedQuantityDeclaration
        workingUnit
        declarationUnit
        totalOutgoingShipmentsLinked
        unassignedQuantityWorking
        unassignedQuantityDeclaration
        order {
          id
          orderNo
          status
          orderStatus
          material { name }
          terminal
          customerAccount { companyname }
          shippingDetails { shippingTo }
          workingUnit
          declarationUnit
          contractedWorkingQuantity
          contractedDeclarationQuantity
          shippedWorkingQuantity
          shippedDeclarationQuantity
          remainingToShipWorkingQuantity
          remainingToShipDeclarationQuantity
          allocatedWorkingQuantity
          allocatedDeclarationQuantity
          remainingToAllocateWorkingQuantity
          remainingToAllocateDeclarationQuantity
          tolerance
          ghgEmission
          ghgSaving
          rawMaterialType
          countryOfOrigin
          feedstockCategory
          preferredAllocation
          mandatory { maxGhgEmission minGhgSaving rawMaterialType countryOfOrigin }
          dispatchDate
          discrepancyCount
        }
      }
      pageInfo { total page limit hasNextPage hasPreviousPage isEmpty }
    }
  }
`;

// Link shipments ▸ Step 1, from an ORDER — the mirror read, keyed by the order alone.
export const LINKABLE_SHIPMENTS_FOR_ORDER_QUERY = `
  query LinkableOutgoingShipmentsForOrder($orderId: ID!, $limit: Int, $page: Int, $filters: OutgoingShipmentFilters, $search: OutgoingShipmentSearch, $orderBy: [OutgoingShipmentOrderBy!]) {
    linkableOutgoingShipmentsForOrder(orderId: $orderId, limit: $limit, page: $page, filters: $filters, search: $search, orderBy: $orderBy) {
      nodes {
        id
        shipmentNo
        status
        material { name }
        site { siteName }
        terminal
        customerAccount { companyname }
        shippingDetails { shippingTo }
        workingUnit
        declarationUnit
        totalQuantityWorking
        totalQuantityDeclaration
        allocatedQuantityWorking
        allocatedQuantityDeclaration
        remainingToAllocateQuantityWorking
        remainingToAllocateQuantityDeclaration
        unassignedQuantityWorking
        unassignedQuantityDeclaration
        linkedOrders { id orderNo }
        ghgEmission
        ghgSaving
        rawMaterialType
        countryOfOrigin
        feedstockCategory
        preferredAllocation
        dispatchDate
        discrepancyCount
      }
      pageInfo { total page limit hasNextPage hasPreviousPage isEmpty }
    }
  }
`;

export const LINKABLE_CONTRACTS_FOR_SHIPMENT_QUERY = `
  query LinkableContractsForShipment($shipmentId: ID!, $limit: Int, $page: Int, $search: ContractSearch, $filters: ContractFilters, $orderBy: [ContractOrderBy!]) {
    linkableContractsForShipment(shipmentId: $shipmentId, limit: $limit, page: $page, search: $search, filters: $filters, orderBy: $orderBy) {
      nodes {
        group
        linkedShipmentIds
        sustainabilityMismatches { attribute message }
        assignedQuantityWorking
        assignedQuantityDeclaration
        assignedQuantityTracking
        contract {
          id
          contractId
          status
          linkedShipmentCount
          workingUnit
          declarationUnit
          workingQuantity
          declarationQuantity
          contractedQuantity
          contractedQuantityUnit
          shippedWorkingQuantity
          shippedDeclarationQuantity
          outstandingWorkingQuantity
          outstandingDeclarationQuantity
          ghgEmissions
          ghgEmissionSavings
          rawMaterialType
          countryOfOrigin
          feedstockCategory
          preferredAllocation
          deliverBy
          discrepancyCount
          discrepancies { items { type message attribute contractValue shipmentValue } }
        }
      }
      pageInfo { total page limit hasNextPage hasPreviousPage isEmpty }
    }
  }
`;

export const SHIPMENT_DISCREPANCY_QUERY = `
  query ShipmentDiscrepancy($id: ID!) {
    shipment(id: $id) {
      id
      shipmentId
      material { name }
      sentBySupplier { companyname }
      receivedAtSite { siteName }
      terminal
      workingUnit
      declarationUnit
      trackingUnit
      shipmentQuantityWorking
      shipmentQuantityDeclaration
      shipmentQuantityTracking
      receivedDeclarationQuantityWorking
      receivedDeclarationQuantityDeclaration
      receivedDeclarationQuantityTracking
      availableShipmentQuantityWorking
      availableShipmentQuantityDeclaration
      availableShipmentQuantityTracking
      discrepancies { type message attribute refKind contractId declarationId daysLeft }
    }
  }
`;

// TODO: the server has no `linkableShipmentsForDeclaration` yet — wire this up once it ships.
export const LINKABLE_SHIPMENTS_FOR_DECLARATION_QUERY = `
  query LinkableShipmentsForDeclaration($declarationId: ID!, $limit: Int, $page: Int, $search: ShipmentSearch, $filters: ShipmentFilters) {
    linkableShipmentsForDeclaration(declarationId: $declarationId, limit: $limit, page: $page, search: $search, filters: $filters) {
      nodes {
        assignedQuantityWorking
        assignedQuantityDeclaration
        assignedQuantityTracking
        shipment {
          id
          shipmentId
          workingUnit
          declarationUnit
          trackingUnit
          totalContractsLinked
          shipmentQuantityWorking
          shipmentQuantityDeclaration
          shipmentQuantityTracking
          unassignedContractQuantityWorking
          unassignedContractQuantityDeclaration
          unassignedContractQuantityTracking
          ghgEmissions
          ghgEmissionSavings
          rawMaterialType
          countryOfOrigin
          feedstockCategory
          preferredAllocation
          dateOfDispatch
          discrepancyCount
        }
      }
      pageInfo { total page limit hasNextPage hasPreviousPage isEmpty }
    }
  }
`;

// The server's cap; the wizards draw every candidate in one unpaginated table.
export const LINKABLE_LIMIT = 100;

// Save routes; see LinkWizard/CLAUDE.md.
const PATHS = {
  // Candidates filtered by the form's own supplier / material / site — no contract id.
  linkShipments: (module, contractId) => `/${module}/contracts/${contractId}/link-shipments`,
  linkContracts: (module, shipmentId) => `/${module}/contracts/batch-group/${shipmentId}/link`,
  // TODO: no declaration link route exists server-side yet — wire this up once it ships.
  linkDeclarationShipments: (module, declarationId) =>
    `/${module}/declarations/${declarationId}/link-shipments`,
  linkOrders: (module, shipmentId) =>
    `/${module}/pending-orders/shipments/${shipmentId}/link-orders`,
  linkShipmentsToOrder: (module, orderId) => `/${module}/pending-orders/${orderId}/link-shipments`,
};

// The shipments a link save touched: the caller's list, else the payload's own batch groups.
export const linkedShipmentIds = ({ shipmentIds, links } = {}) =>
  (shipmentIds ?? (links ?? []).map((link) => link.batchGroupId)).map(String);

export const linksApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Link shipments ▸ Step 1 — re-read on every open; see components/common/LinkWizard.
    getLinkableShipments: build.query({
      query: ({ contractId, page, pageSize, search, filters }) => ({
        document: LINKABLE_SHIPMENTS_FOR_CONTRACT_QUERY,
        variables: {
          contractId,
          page,
          limit: pageSize ?? LINKABLE_LIMIT,
          search: buildShipmentSearchInput(search),
          filters: buildShipmentFiltersInput(filters),
        },
      }),
      transformResponse: (data) => processLinkableShipments(data.linkableShipmentsForContract),
      providesTags: (result, error, arg) => [linkableShipmentsTag(arg.contractId)],
      keepUnusedDataFor: 0,
    }),

    // Link shipments for a DECLARATION ▸ Step 1 — the contract read with a different key.
    getLinkableShipmentsForDeclaration: build.query({
      query: ({ declarationId, page, pageSize, search, filters }) => ({
        document: LINKABLE_SHIPMENTS_FOR_DECLARATION_QUERY,
        variables: {
          declarationId,
          page,
          limit: pageSize ?? LINKABLE_LIMIT,
          search: buildShipmentSearchInput(search),
          filters: buildShipmentFiltersInput(filters),
        },
      }),
      transformResponse: (data) => processLinkableShipments(data.linkableShipmentsForDeclaration),
      providesTags: (result, error, arg) => [linkableShipmentsForDeclarationTag(arg.declarationId)],
      keepUnusedDataFor: 0,
    }),

    // N shipments → ONE declaration.
    linkDeclarationShipments: build.mutation({
      query: ({ module, declarationId, links }) => ({
        url: PATHS.linkDeclarationShipments(module, declarationId),
        method: 'POST',
        body: { shipmentLinks: links },
      }),
      transformErrorResponse: processServerError,
      onQueryStarted: notifyOnSuccess('LINK_SHIPMENTS_TO_DECLARATION_SAVED'),
      invalidatesTags: (result, error, arg) => [
        { type: TAG_TYPES.Declaration, id: arg.declarationId },
        { type: TAG_TYPES.Declaration, id: 'LIST' },
        { type: TAG_TYPES.Shipment, id: 'LIST' },
        linkedShipmentsForDeclarationTag(arg.declarationId),
      ],
    }),

    // Link contracts ▸ Step 1 — the mirror read.
    getLinkableContracts: build.query({
      query: ({ shipmentId, page, pageSize, search, filters }) => ({
        document: LINKABLE_CONTRACTS_FOR_SHIPMENT_QUERY,
        variables: {
          shipmentId,
          page,
          limit: pageSize ?? LINKABLE_LIMIT,
          search: buildContractSearchInput(search),
          filters: buildContractFiltersInput(filters),
        },
      }),
      transformResponse: (data) => processLinkableContracts(data.linkableContractsForShipment),
      providesTags: (result, error, arg) => [linkableContractsTag(arg.shipmentId)],
      keepUnusedDataFor: 0,
    }),

    // Link orders ▸ Step 1 — re-read on every open, same as the two above.
    getLinkableOrders: build.query({
      query: ({ shipmentId, page, pageSize, search, sort }) => ({
        document: LINKABLE_ORDERS_FOR_SHIPMENT_QUERY,
        variables: {
          shipmentId,
          page,
          limit: pageSize ?? LINKABLE_LIMIT,
          search: buildOrderSearchInput(search),
          orderBy: buildLinkableOrderOrderBy(sort),
        },
      }),
      transformResponse: (data) => processLinkableOrders(data.linkableOrdersForOutgoingShipment),
      keepUnusedDataFor: 0,
    }),

    // Link shipments ▸ Step 1, from an order — the mirror read.
    getLinkableShipmentsForOrder: build.query({
      query: ({ orderId, page, pageSize, search, sort }) => ({
        document: LINKABLE_SHIPMENTS_FOR_ORDER_QUERY,
        variables: {
          orderId,
          page,
          limit: pageSize ?? LINKABLE_LIMIT,
          search: buildLinkableShipmentSearchInput(search),
          orderBy: buildLinkableShipmentOrderBy(sort),
        },
      }),
      transformResponse: (data) =>
        processLinkableShipmentsForOrder(data.linkableOutgoingShipmentsForOrder),
      keepUnusedDataFor: 0,
    }),

    // N orders → ONE outgoing shipment.
    linkShipmentOrders: build.mutation({
      query: ({ module, shipmentId, orders }) => ({
        url: PATHS.linkOrders(module, shipmentId),
        method: 'POST',
        body: { orders },
      }),
      transformErrorResponse: processServerError,
      onQueryStarted: notifyOnSuccess('LINK_ORDERS_SAVED'),
      invalidatesTags: (result, error, arg) => [
        { type: TAG_TYPES.OutgoingShipment, id: arg.shipmentId },
        { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
        { type: TAG_TYPES.Order, id: `SHIPMENT-${arg.shipmentId}` },
        { type: TAG_TYPES.Order, id: 'LIST' },
      ],
    }),

    // ONE order → N outgoing shipments. Bulk fires this once per selected order; there is no batch route.
    linkOrderShipments: build.mutation({
      query: ({ module, orderId, shipments }) => ({
        url: PATHS.linkShipmentsToOrder(module, orderId),
        method: 'POST',
        body: { shipments },
      }),
      transformErrorResponse: processServerError,
      invalidatesTags: (result, error, arg) => [
        { type: TAG_TYPES.Order, id: arg.orderId },
        { type: TAG_TYPES.Order, id: 'LIST' },
        { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
        // The order's own "Linked outgoing shipment" tab — what this link just changed.
        { type: TAG_TYPES.OutgoingShipment, id: `ORDER-${arg.orderId}` },
      ],
    }),

    // A candidate row's discrepancy badge — only the fields the popup shows.
    getShipmentDiscrepancy: build.query({
      query: (id) => ({ document: SHIPMENT_DISCREPANCY_QUERY, variables: { id } }),
      transformResponse: (data) => processShipmentDiscrepancy(data.shipment),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Shipment, id }],
    }),

    // N shipments → ONE contract; a shipment can inherit its terminal, so each one refreshes.
    linkContractShipments: build.mutation({
      query: ({ module, contractId, links }) => ({
        url: PATHS.linkShipments(module, contractId),
        method: 'POST',
        body: { shipmentLinks: links },
      }),
      transformErrorResponse: processServerError,
      onQueryStarted: notifyOnSuccess('LINK_SHIPMENTS_SAVED'),
      invalidatesTags: (result, error, arg) => [
        { type: TAG_TYPES.Contract, id: arg.contractId },
        { type: TAG_TYPES.Contract, id: 'LIST' },
        { type: TAG_TYPES.Shipment, id: 'LIST' },
        linkedShipmentsTag(arg.contractId),
        ...linkedShipmentIds(arg).flatMap((id) => [
          { type: TAG_TYPES.Shipment, id },
          linkedContractsTag(id),
        ]),
      ],
    }),

    // ONE shipment → N contracts, the mirror route.
    linkShipmentContracts: build.mutation({
      query: ({ module, shipmentId, links }) => ({
        url: PATHS.linkContracts(module, shipmentId),
        method: 'POST',
        body: { contractLinks: links },
      }),
      transformErrorResponse: processServerError,
      onQueryStarted: notifyOnSuccess('LINK_CONTRACTS_SAVED'),
      invalidatesTags: (result, error, arg) => [
        { type: TAG_TYPES.Shipment, id: arg.shipmentId },
        { type: TAG_TYPES.Shipment, id: 'LIST' },
        { type: TAG_TYPES.Contract, id: 'LIST' },
        linkedContractsTag(arg.shipmentId),
        ...(arg.contractIds ?? []).map(linkedShipmentsTag),
      ],
    }),
  }),
});

export const {
  useGetLinkableShipmentsQuery,
  useGetLinkableShipmentsForDeclarationQuery,
  useGetLinkableContractsQuery,
  useGetLinkableOrdersQuery,
  useGetLinkableShipmentsForOrderQuery,
  useGetShipmentDiscrepancyQuery,
  useLinkContractShipmentsMutation,
  useLinkDeclarationShipmentsMutation,
  useLinkShipmentContractsMutation,
  useLinkShipmentOrdersMutation,
  useLinkOrderShipmentsMutation,
} = linksApi;
